import { Input } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import {
  Control,
  FieldErrors,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTracingContactQueryKey,
  useGetTracingContactSuspense,
  useUpdateTracingContact,
} from '../../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import {
  PatientAddressDtoReferenceType,
  PatientDto,
  TracingContactDetailsDto,
} from '../../../../../api/generated/model';
import type { UpdateContactDetailsDto } from '../../../../../api/generated/model/updateContactDetailsDto.ts';
import {
  getGetPatientBasicsByIdQueryKey,
  getGetPatientByIdQueryKey,
  useGetPatientByIdSuspense,
  useUpdatePatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { SingleLineFormControl } from '../../../../../components/form/SingeLineFormComponents.tsx';
import { LeavePageWarning } from '../../../../../components/LeavePageWarning.tsx';
import { FieldMetaInfos, useAutoSaveForm } from '../../../../../hooks/useAutoSaveForm.ts';
import { getCurrentDate } from '../../../../../utils/date.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';
import { PersonDataFormFields, PersonDataFormValues } from '../../../../masterdata/components/PersonDataFormFields.tsx';

export function EditTracingContactDetailsForm({ tracingContactId }: { tracingContactId: string }) {
  const queryClient = useQueryClient();
  const { data: patient } = useGetPatientByIdSuspense(tracingContactId);
  const { data: tracingContactDetails } = useGetTracingContactSuspense(tracingContactId);

  const updatePatientMutation = useUpdatePatient({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetPatientByIdQueryKey(patient.id), data);
        void queryClient.invalidateQueries({ queryKey: getGetPatientBasicsByIdQueryKey(patient.id) });
        void queryClient.invalidateQueries({ queryKey: getGetTracingContactQueryKey(tracingContactId) });
      },
      onError: () =>
        toast.error('Bei dem Speichern der Personalien ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });
  const updateTracingContactMutation = useUpdateTracingContact({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetTracingContactQueryKey(tracingContactId), data);
      },
      onError: () =>
        toast.error('Bei dem Speichern der Contact-Tracing-Personalien ist ein Fehler aufgetreten', {
          toastId: 'saveTracingContactError',
        }),
    },
  });

  const {
    register,
    control,
    setValue,
    getValues,
    getFieldState,
    formState: { errors, isFormDirty },
    scheduleSave,
  } = useAutoSaveForm({
    sourceData: { patient, tracingContactDetails },
    getDefaultValues,
    save: async (data) => {
      const [updatedPatient, updatedTracingContactDetails] = await Promise.all([
        updatePatientMutation.mutateAsync({
          data: mapFormValuesToPatientPayload(data, patient),
        }),
        updateTracingContactMutation.mutateAsync({
          id: tracingContactId,
          data: mapFormValuesToTracingContactPayload(data, tracingContactDetails),
        }),
      ]);

      return {
        patient: updatedPatient,
        tracingContactDetails: updatedTracingContactDetails,
      };
    },
    fieldMetaInfos,
    validationMode: 'onChange',
    onValidationFailed: () =>
      toast.warn('Personalien können aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('validationError'),
  });

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updatePatientMutation.isPending && !updateTracingContactMutation.isPending,
    withResolver: true,
  });

  return (
    <>
      <PersonDataFormFields
        register={register as unknown as UseFormRegister<PersonDataFormValues>}
        control={control as unknown as Control<PersonDataFormValues>}
        setValue={setValue as unknown as UseFormSetValue<PersonDataFormValues>}
        getValues={getValues as unknown as UseFormGetValues<PersonDataFormValues>}
        getFieldState={getFieldState as unknown as UseFormGetFieldState<PersonDataFormValues>}
        errors={errors as FieldErrors<PersonDataFormValues>}
        duplicateErrors={{}}
        checkDuplicateField={() => undefined}
        referencePersonSelect={{
          mode: 'edit',
          personId: tracingContactId,
          createEndpoint: 'patient',
          fetchedOptions: patient.bezugspersonen ?? [],
        }}
        contactPatientRelationshipStatusField={
          <SingleLineFormControl<TracingContactPersonFormValues>
            label="Beziehung zum IP"
            fieldState={getFieldState('contactPatientRelationshipStatus')}>
            <Input {...register('contactPatientRelationshipStatus')} variant="plain" />
          </SingleLineFormControl>
        }
      />

      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onDelete={() => blocker.proceed?.()}
        onSave={() => {
          blocker.reset?.();
          scheduleSave();
        }}
        title={`Daten von ${patient.firstName} ${patient.lastName} behalten?`}
      />
    </>
  );
}

type TracingContactDetailsFormSourceData = {
  patient: PatientDto;
  tracingContactDetails: TracingContactDetailsDto;
};

type TracingContactPersonFormValues = PersonDataFormValues & {
  contactPatientRelationshipStatus: string;
};

type PatientDtoWithReferencePersonIds = PatientDto & {
  bezugspersonIds: string[];
};

function getDefaultValues({
  patient,
  tracingContactDetails,
}: TracingContactDetailsFormSourceData): TracingContactPersonFormValues {
  return {
    firstName: patient.firstName ?? '',
    lastName: patient.lastName ?? '',
    birthDate: patient.birthDate ?? '',
    gender: patient.gender ?? null,
    mobilePhoneNumber: patient.mobilePhoneNumber || patient.phone || '',
    privatePhoneNumber: patient.privatePhoneNumber ?? '',
    workPhoneNumber: patient.workPhoneNumber ?? '',
    email: patient.email ?? '',
    nationality: patient.nationality ?? null,
    origin: patient.origin ?? null,
    arrival: patient.arrival ?? '',
    asylumNumber: patient.asylumNumber ?? '',
    zemisNumber: patient.zemisNumber ?? '',
    languages: patient.languages ?? [],
    street: patient.street ?? '',
    additionalInformation: patient.additionalInformation ?? '',
    bezugspersonen: patient.bezugspersonen ?? [],
    residencyPermit: patient.residencyPermit ?? null,
    zip: patient.postalCode ? String(patient.postalCode) : '',
    city: patient.city ?? '',
    canton: patient.state ?? '',
    address: null,
    contact: null,
    salutation: patient.salutation ?? '',
    livingStatus: patient.livingStatus ?? null,
    note: patient.note ?? '',
    contactPatientRelationshipStatus: tracingContactDetails.contactPatientRelationshipStatus ?? '',
  };
}

function mapFormValuesToPatientPayload(
  data: TracingContactPersonFormValues,
  patient: PatientDto,
): PatientDtoWithReferencePersonIds {
  const mobilePhoneNumber = getStringOrUndefined(data.mobilePhoneNumber);

  return {
    ...patient,
    salutation: data.salutation || null,
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    birthDate: data.birthDate,
    gender: data.gender ?? patient.gender,
    livingStatus: data.livingStatus ?? null,
    note: getStringOrUndefined(data.note) ?? null,
    phone: mobilePhoneNumber,
    mobilePhoneNumber,
    privatePhoneNumber: getStringOrUndefined(data.privatePhoneNumber),
    workPhoneNumber: getStringOrUndefined(data.workPhoneNumber),
    email: getStringOrUndefined(data.email),
    nationality: data.nationality ?? undefined,
    origin: data.origin ?? undefined,
    arrival: getStringOrUndefined(data.arrival),
    asylumNumber: getStringOrUndefined(data.asylumNumber),
    zemisNumber: getStringOrUndefined(data.zemisNumber),
    languages: data.languages ?? [],
    residencyPermit: data.residencyPermit ?? patient.residencyPermit,
    postalCode: getStringOrUndefined(data.zip),
    city: getStringOrUndefined(data.city),
    state: getStringOrUndefined(data.canton),
    street: getStringOrUndefined(data.street),
    additionalInformation: getStringOrUndefined(data.additionalInformation),
    bezugspersonen: data.bezugspersonen ?? [],
    bezugspersonIds: (data.bezugspersonen ?? [])
      .filter((item) => item.isSelected && item.id)
      .map((item) => item.id as string),
    address: data.address
      ? {
          address: data.address,
          contact: data.contact ?? undefined,
          from: getCurrentDate(),
          patientId: patient.id,
          referenceType: PatientAddressDtoReferenceType.PATIENT_RESIDENCE,
        }
      : patient.address,
  };
}

function mapFormValuesToTracingContactPayload(
  data: TracingContactPersonFormValues,
  tracingContactDetails: TracingContactDetailsDto,
): UpdateContactDetailsDto {
  return {
    salutation: data.salutation || null,
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    gender: (data.gender ?? tracingContactDetails.gender) as UpdateContactDetailsDto['gender'],
    livingStatus: data.livingStatus ?? null,
    note: getStringOrUndefined(data.note) ?? null,
    languages: data.languages ?? [],
    birthDate: data.birthDate,
    contactPatientRelationshipStatus: getStringOrUndefined(data.contactPatientRelationshipStatus),
    phone: getStringOrUndefined(data.mobilePhoneNumber),
    email: getStringOrUndefined(data.email),
    nationality: data.nationality ?? undefined,
    asylumNumber: getStringOrUndefined(data.asylumNumber),
  };
}

const fieldMetaInfos: FieldMetaInfos<TracingContactPersonFormValues> = {
  firstName: { debounce: true },
  lastName: { debounce: true },
  birthDate: { debounce: true },
  gender: { debounce: false },
  salutation: { debounce: false },
  livingStatus: { debounce: false },
  note: { debounce: true },
  mobilePhoneNumber: { debounce: true },
  privatePhoneNumber: { debounce: true },
  workPhoneNumber: { debounce: true },
  email: { debounce: true },
  nationality: { debounce: false },
  origin: { debounce: false },
  arrival: { debounce: true },
  asylumNumber: { debounce: true },
  zemisNumber: { debounce: true },
  languages: { debounce: false },
  residencyPermit: { debounce: false },
  zip: { debounce: true },
  city: { debounce: true },
  canton: { debounce: true },
  street: { debounce: true },
  additionalInformation: { debounce: true },
  address: { debounce: true },
  contact: { debounce: true },
  bezugspersonen: { debounce: false, ignorePropertiesForComparison: ['id', 'isSelected', 'salutation'] },
  contactPatientRelationshipStatus: { debounce: true },
};
